/**
 * Surprav Theme — Wishlist System
 *
 * Client-side wishlist using localStorage for anonymous visitors.
 * Emits PubSub events on add/remove for State Store and Telemetry Layer
 * integration. Metafield sync for logged-in customers is handled by
 * the separate wishlist-sync.js module.
 *
 * Public API exposed as window.SurpravWishlist:
 *   - add(productId)    — add product to wishlist
 *   - remove(productId) — remove product from wishlist
 *   - has(productId)    — check if product is in wishlist
 *   - getAll()          — get all wishlist items
 *   - count()           — get wishlist count
 *   - clear()           — clear wishlist
 */

(function () {
  'use strict';

  var STORAGE_KEY = 'surprav_wishlist';

  // ── localStorage helpers ─────────────────────────────────

  /**
   * Read wishlist items from localStorage.
   * @returns {Array<{productId: string, addedAt: string}>}
   */
  function loadItems() {
    try {
      var raw = localStorage.getItem(STORAGE_KEY);
      if (!raw) return [];
      var parsed = JSON.parse(raw);
      return Array.isArray(parsed) ? parsed : [];
    } catch (_e) {
      return [];
    }
  }

  /**
   * Persist wishlist items to localStorage.
   * @param {Array<{productId: string, addedAt: string}>} items
   */
  function saveItems(items) {
    try {
      localStorage.setItem(STORAGE_KEY, JSON.stringify(items));
    } catch (_e) {
      // quota exceeded or unavailable — silently ignore
    }
  }

  // ── PubSub helper ────────────────────────────────────────

  /**
   * Emit a PubSub event via the global publish() function (pubsub.js)
   * or Surprav.PubSub as fallback.
   * @param {string} eventName
   * @param {*} data
   */
  function emit(eventName, data) {
    if (typeof publish === 'function') {
      publish(eventName, data);
    } else if (
      window.Surprav &&
      Surprav.PubSub &&
      typeof Surprav.PubSub.publish === 'function'
    ) {
      Surprav.PubSub.publish(eventName, data);
    }
  }

  // ── Wishlist API ─────────────────────────────────────────

  var SurpravWishlist = {
    /**
     * Add a product to the wishlist. No-op if already present.
     * @param {string} productId
     * @returns {boolean} true if added, false if already present
     */
    add: function (productId) {
      if (!productId) return false;
      var id = String(productId);
      var items = loadItems();

      for (var i = 0; i < items.length; i++) {
        if (String(items[i].productId) === id) return false;
      }

      var entry = { productId: id, addedAt: new Date().toISOString() };
      items.push(entry);
      saveItems(items);

      emit('wishlist:add', { productId: id, item: entry });
      emit('wishlist:change', { action: 'add', productId: id, items: items });

      SurpravWishlist._syncButtons(id, true);
      return true;
    },

    /**
     * Remove a product from the wishlist.
     * @param {string} productId
     * @returns {boolean} true if removed, false if not found
     */
    remove: function (productId) {
      if (!productId) return false;
      var id = String(productId);
      var items = loadItems();
      var filtered = [];
      var found = false;

      for (var i = 0; i < items.length; i++) {
        if (String(items[i].productId) === id) {
          found = true;
        } else {
          filtered.push(items[i]);
        }
      }

      if (!found) return false;

      saveItems(filtered);

      emit('wishlist:remove', { productId: id });
      emit('wishlist:change', { action: 'remove', productId: id, items: filtered });

      SurpravWishlist._syncButtons(id, false);
      return true;
    },

    /**
     * Check if a product is in the wishlist.
     * @param {string} productId
     * @returns {boolean}
     */
    has: function (productId) {
      if (!productId) return false;
      var id = String(productId);
      var items = loadItems();
      for (var i = 0; i < items.length; i++) {
        if (String(items[i].productId) === id) return true;
      }
      return false;
    },

    /**
     * Get all wishlist items.
     * @returns {Array<{productId: string, addedAt: string}>}
     */
    getAll: function () {
      return loadItems();
    },

    /**
     * Get the number of items in the wishlist.
     * @returns {number}
     */
    count: function () {
      return loadItems().length;
    },

    /**
     * Clear the entire wishlist.
     */
    clear: function () {
      saveItems([]);
      emit('wishlist:change', { action: 'clear', productId: null, items: [] });
      SurpravWishlist._syncAllButtons();
    },

    // ── DOM helpers (internal) ─────────────────────────────

    /**
     * Toggle the .is-active class on all wishlist buttons for a product.
     * @param {string} productId
     * @param {boolean} active
     * @private
     */
    _syncButtons: function (productId, active) {
      var buttons = document.querySelectorAll(
        '[data-wishlist-btn][data-product-id="' + productId + '"],' +
        '.deals-row__wishlist[data-product-id="' + productId + '"],' +
        '.product-card__wishlist[data-product-id="' + productId + '"]'
      );
      for (var i = 0; i < buttons.length; i++) {
        if (active) {
          buttons[i].classList.add('is-active');
          buttons[i].setAttribute('aria-label', 'Verwijderen uit wishlist');
        } else {
          buttons[i].classList.remove('is-active');
          buttons[i].setAttribute('aria-label', 'Toevoegen aan wishlist');
        }
      }
    },

    /**
     * Sync all wishlist buttons on the page with current state.
     * @private
     */
    _syncAllButtons: function () {
      var buttons = document.querySelectorAll(
        '[data-wishlist-btn][data-product-id],' +
        '.deals-row__wishlist[data-product-id],' +
        '.product-card__wishlist[data-product-id]'
      );
      for (var i = 0; i < buttons.length; i++) {
        var pid = buttons[i].getAttribute('data-product-id');
        if (SurpravWishlist.has(pid)) {
          buttons[i].classList.add('is-active');
          buttons[i].setAttribute('aria-label', 'Verwijderen uit wishlist');
        } else {
          buttons[i].classList.remove('is-active');
          buttons[i].setAttribute('aria-label', 'Toevoegen aan wishlist');
        }
      }
    },

    /**
     * Handle click on a wishlist button — toggle add/remove.
     * @param {Event} event
     * @private
     */
    _handleClick: function (event) {
      var button = event.currentTarget;
      var productId = button.getAttribute('data-product-id');
      if (!productId) return;

      if (SurpravWishlist.has(productId)) {
        SurpravWishlist.remove(productId);
      } else {
        SurpravWishlist.add(productId);
      }
    },

    /**
     * Attach click handlers to all wishlist buttons
     * and sync their initial state.
     * @private
     */
    _init: function () {
      var buttons = document.querySelectorAll(
        '[data-wishlist-btn][data-product-id],' +
        '.deals-row__wishlist[data-product-id],' +
        '.product-card__wishlist[data-product-id]'
      );
      for (var i = 0; i < buttons.length; i++) {
        buttons[i].addEventListener('click', SurpravWishlist._handleClick);
      }
      SurpravWishlist._syncAllButtons();
    }
  };

  // ── Expose globally ──────────────────────────────────────

  window.SurpravWishlist = SurpravWishlist;

  // ── Auto-initialize on DOM ready ─────────────────────────

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', SurpravWishlist._init);
  } else {
    SurpravWishlist._init();
  }
})();
